<template lang="pug">
v-card(flat)
	client-only
		mapbox.mt-6(
			ref="map"
			style="height:600px"
			:layers="layers"
			:sources="sources"
			:fitbounds="sources['map-areas-locations'].data.features.map(e=>e.geometry.coordinates)"
			@map-styleload="mapStyle"
			)
</template>

<script>
import gql from 'graphql-tag';
import debounce from 'lodash/debounce';
import MapEmbed from '@dengl/embeds/map/embed.vue';

const query = gql`
	query AreasForGeo($filters: String, $limit: Int) {
		areas: areasElastic(filters: $filters, limit: $limit) {
			edges {
				node {
					id
					name
					slug
					geography {
						location {
							type
							coordinates
						}
						polygon {
							type
							coordinates
						}
					}
				}
			}
		}
	}
`;

const { decodeFilters } = require('@dengl/common/utils/filterstrings');

export default {
	components: {
		MapEmbed,
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	props: {
		filterstring: {
			type: String,
			default: ''
		},

		limit: {
			type: [String, Number],
			default: 500
		}
	},
	data() {
		return {
			areas: [],
			canBoundsChange: true
		};
	},
	async fetch() {
		const limit = this.ignoreLimit ? 1000 : parseInt(this.limit);
		this.ignoreLimit = false;

		this.areas = await this.$graphClient
			.query({
				query,
				variables: {
					filters: this.filterstring,
					limit
				},
				fetchPolicy: 'no-cache'
			})
			.then(({ data }) => {
				return data.areas.edges.map(a => a.node).filter(area => area);
			});
	},
	computed: {
		sources() {
			return {
				'map-areas-polygons': {
					type: 'geojson',
					data: {
						type: 'FeatureCollection',
						features: this.areas.map((area, index) => {
							return {
								type: 'Feature',
								id: index + 10,
								geometry: area.geography.polygon,
								properties: {
									...area,
									popup: `<h3>${area.name}</h3>`
								}
							};
						})
					}
				},
				'map-areas-locations': {
					type: 'geojson',
					data: {
						type: 'FeatureCollection',
						features: this.areas.map((area, index) => {
							return {
								type: 'Feature',
								id: index + 10,
								geometry: area.geography.location,
								properties: {
									...area,
									popup: `<h3>${area.name}</h3>`
								}
							};
						})
					}
				}
			};
		},
		layers() {
			return [
				{
					id: 'map-areas-polygons',
					type: 'fill',
					source: 'map-areas-polygons',
					fit: true,
					defaultevents: true,
					layout: {
						visibility: 'visible'
					},
					minzoom: 8,
					maxzoom: 24,
					paint: {
						'fill-color': '#FFAA5F',
						'fill-outline-color': '#555555',
						'fill-opacity': [
							'case',
							['boolean', ['feature-state', 'hover'], false],
							1,
							0.8
						]
					}
				},
				{
					id: 'map-areas-locations',
					type: 'circle',
					source: 'map-areas-locations',
					minzoom: 0,
					maxzoom: 8,
					defaultevents: true,
					layout: {
						visibility: 'visible'
					},
					paint: {
						'circle-radius': [
							'case',
							['boolean', ['feature-state', 'hover'], false],
							7,
							5
						],
						'circle-color': 'rgb(79, 164, 221)',
						'circle-stroke-color': '#fff',
						'circle-stroke-width': 2
					}
				}
			];
		}
	},
	watch: {
		filterstring(value, oldValue) {
			this.$fetchState.pending = true;
			this.debouncedFetch();
			const newFilters = decodeFilters(value, this.$i18n.locale);
			const oldFilters = decodeFilters(oldValue, this.$i18n.locale);

			newFilters.countries = newFilters.countries || [];
			oldFilters.countries = oldFilters.countries || [];

			this.canBoundsChange =
				newFilters.countries.length !== oldFilters.countries.length;
		},
		sources() {
			if (this.$refs.map && this.canBoundsChange) {
				this.$nextTick(() => {
					this.$refs.map.fitToCurrentBounds();
				});
			}
		}
	},
	methods: {
		debouncedFetch: debounce(function () {
			this.$fetch();
		}, 200),
		fetchAll() {
			this.ignoreLimit = true;
			this.$fetch();
		},
		mapStyle(map) {
			map.on('click', 'map-areas-locations', e => {
				if (e.features.length > 0) {
					if (e.features[0].id) {
						this.mouseClick(e.features[0]);
					}
				}
			});
			map.on('click', 'map-areas-polygons', e => {
				if (e.features.length > 0) {
					if (e.features[0].id) {
						this.mouseClick(e.features[0]);
					}
				}
			});
		},
		mouseClick(feature) {
			this.$router.push(
				this.localePath({
					name: 'areas-area',
					params: { area: feature.properties.slug }
				})
			);
		}
	}
};
</script>
